import { useState } from 'react';
import { stanNotify } from '@/lib/stanNotify';
import { mainApi } from '@/lib';
import { connectInstagram } from '@/lib/utils/instagramConnect';
import { connectGoogleCalendar, connectZoom } from '@/lib/utils/googleCalendarConnect';
import type { Integration } from '../types/integrations.types';
import type { IntegrationsResponse } from '@/types';

interface UseIntegrationActionsParams {
    userIntegrations: IntegrationsResponse | undefined;
    refetchIntegrations: () => void;
}

/**
 * Hook that provides connect/disconnect actions for integrations
 */
export function useIntegrationActions({ userIntegrations, refetchIntegrations }: UseIntegrationActionsParams) {
    const [loadingIntegrations, setLoadingIntegrations] = useState<Set<string>>(new Set());

    const handleConnect = async (integration: Integration) => {
        // Handle Instagram with popup
        if (integration.id === 'instagram') {
            connectInstagram(() => {
                refetchIntegrations();
            });
            return;
        }

        // Handle Google Calendar and Zoom with full page redirect
        if (integration.id === 'google-calendar') {
            const returnUrl = window.location.href;
            await connectGoogleCalendar(returnUrl);
            return;
        }

        if (integration.id === 'zoom') {
            const returnUrl = window.location.href;
            await connectZoom(returnUrl);
            return;
        }

        // For other integrations (like Zapier), open documentation
        if (integration.documentationUrl) {
            window.open(integration.documentationUrl, '_blank');
        }
    };

    const handleDisconnect = async (integration: Integration) => {
        setLoadingIntegrations(prev => new Set(prev).add(integration.id));

        try {
            // Find the integration from backend data
            const integrationData = userIntegrations
                ? Object.values(userIntegrations).find(int => int.type === integration.id)
                : null;

            if (!integrationData?.integration_id) {
                throw new Error('No Integration ID found');
            }

            // Delete the integration
            await mainApi.delete(`v1/integrations/${integrationData.integration_id}`);

            stanNotify({
                type: 'success',
                title: 'Success!',
                text: `${integration.title} has been disconnected successfully.`,
            });

            // Refetch integrations to update UI
            refetchIntegrations();
        } catch (error) {
            console.error('Error disconnecting integration:', error);
            stanNotify({
                type: 'error',
                title: 'Oops..',
                text: `We could not disconnect your ${integration.title}. Please contact our support and we will help you!`,
            });
        } finally {
            setLoadingIntegrations(prev => {
                const next = new Set(prev);
                next.delete(integration.id);
                return next;
            });
        }
    };

    return {
        loadingIntegrations,
        handleConnect,
        handleDisconnect,
    };
}
